/** The pieces every inline editor on Brand Guidelines' page is made of (ORB-971, ORB-973). */

import type { ComponentChildren } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { Pencil, Plus, Trash2 } from 'lucide-preact'
import {
  FORM_ERROR,
  entryLabel,
  openerOf,
  tokenEditorChanged,
  tokenNoun,
  type TokenEditor,
  type TokenKind,
} from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { GHOST_SMALL, ICON_BUTTON, QUIET_BUTTON, ROLE_PILL, SAVE_SMALL } from './brandGuidelinesStyles'
import type { Pointers } from './brandEditing'

export function RolePills({ name, pointers }: { name: string; pointers: Pointers }) {
  return <>{pointers.get(name)?.map(role => <span key={role} className={ROLE_PILL}>{role}</span>)}</>
}

/**
 * Change and Remove for one entry, named as its row names it: "Change coral-500", "Remove the heading family". Where a
 * row has no room for the words, the buttons show only their icons and keep the words as their names.
 */
export function RowActions({ kind, name, pending, iconOnly = false, onChange, onRemove }: {
  kind: TokenKind
  name: string
  pending: boolean
  iconOnly?: boolean
  /** Absent for an entry that can only be removed. */
  onChange?: () => void
  onRemove: () => void
}) {
  const subject = entryLabel(kind, name)
  const className = iconOnly ? ICON_BUTTON : QUIET_BUTTON
  const icon = iconOnly ? 'w-3.5 h-3.5' : 'w-3 h-3'
  return (
    <span className="inline-flex items-center gap-0.5">
      {onChange && (
        <button type="button" className={className} disabled={pending} aria-label={`Change ${subject}`}
          title={iconOnly ? `Change ${subject}` : undefined} data-opener={openerOf('change', kind, name)} onClick={onChange}>
          <Pencil className={icon} aria-hidden="true" />{!iconOnly && 'Change'}
        </button>
      )}
      <button type="button" className={className} disabled={pending} aria-label={`Remove ${subject}`}
        title={iconOnly ? `Remove ${subject}` : undefined} data-opener={openerOf('remove', kind, name)} onClick={onRemove}>
        <Trash2 className={icon} aria-hidden="true" />{!iconOnly && 'Remove'}
      </button>
    </span>
  )
}

/** Asked before an entry is removed. Focus starts on Keep, the answer that changes nothing. */
export function RemoveQuestion({ kind, name, pending, className = '', onRemove, onKeep }: {
  kind: TokenKind
  name: string
  pending: boolean
  className?: string
  onRemove: () => void
  onKeep: () => void
}) {
  const keep = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    keep.current?.focus()
  }, [])
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      <span className="text-xs text-slate-700 dark:text-slate-300">Remove {entryLabel(kind, name)}?</span>
      <button type="button" className={`${QUIET_BUTTON} text-red-600 dark:text-red-400`} disabled={pending} onClick={onRemove}>Remove</button>
      <button ref={keep} type="button" className={QUIET_BUTTON} onClick={onKeep}>Keep</button>
    </span>
  )
}

/** The API's refusal of a removal, shown in the entry's own row. */
export function Refusal({ message }: { message: string }) {
  return <p role="alert" className="text-sm text-amber-700 dark:text-amber-300">{message}</p>
}

/** The quiet button that opens an add form, at the end of a list or group. */
export function AddButton({ label, ariaLabel, kind, slot, pending, onClick }: {
  label: string
  ariaLabel?: string
  kind: TokenKind
  /** The group the new entry joins, when its kind is listed in groups. */
  slot: string | null
  pending: boolean
  onClick: () => void
}) {
  return (
    <div>
      <button type="button" className={`${QUIET_BUTTON} -ml-1.5`} disabled={pending} aria-label={ariaLabel}
        data-opener={openerOf('add', kind, slot)} onClick={onClick}>
        <Plus className="w-3 h-3" aria-hidden="true" />{label}
      </button>
    </div>
  )
}

/**
 * An entry's form, in place of the entry or its Add button. The button that opened it is replaced by it, so focus
 * moves into its first field. Save waits for a change, since saving an entry unchanged would still move the set's time
 * and ask for its confirmation again; Add is always there to press.
 */
export function InlineForm({ editor, heading, pending, note, onCancel, onSave, children }: {
  editor: TokenEditor
  heading?: string
  pending: boolean
  /** What else changes when this is saved, said beside Save. */
  note?: string | null
  onCancel: () => void
  onSave: () => void
  children: ComponentChildren
}) {
  const adding = editor.current === null
  const canSave = adding || tokenEditorChanged(editor)
  const error = editor.errors[FORM_ERROR]
  const form = useRef<HTMLFormElement>(null)
  useEffect(() => {
    form.current?.querySelector<HTMLElement>('input, select')?.focus()
  }, [])

  return (
    <form ref={form} noValidate
      aria-label={heading ?? (adding ? `Add a ${tokenNoun(editor.kind)}` : `Change ${entryLabel(editor.kind, editor.current ?? '')}`)}
      className="my-2 p-4 flex flex-col gap-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40"
      onSubmit={event => { event.preventDefault(); if (canSave) onSave() }}>
      {heading && <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{heading}</h3>}
      {children}
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className={SAVE_SMALL} disabled={pending || !canSave}>
          {pending ? 'Saving...' : adding ? 'Add' : 'Save'}
        </button>
        <button type="button" className={GHOST_SMALL} disabled={pending} onClick={onCancel}>Cancel</button>
        {note && <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">{note}</span>}
      </div>
    </form>
  )
}
